import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { CitationItem, CitationWithId } from '@sciflow/cite';
import { SFNodeType } from '@sciflow/schema';
import { Apollo } from 'apollo-angular';
import { DOMSerializer, Fragment, Node as ProsemirrorNode } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';
import { EditorView, NodeViewConstructor } from 'prosemirror-view';
import { firstValueFrom } from 'rxjs';
import { CitationView } from '../prosemirror/citation.view';
import {
  updateCitationByIdCommand,
  updateCitationByIdSilentCommand,
} from '../prosemirror/commands';
import { getReferencePlugin } from '../prosemirror/references.plugin';
import { RenderingService } from '../rendering.service';
import { Reference } from '../reference.model';
import { addReferences, updateCitation } from '../store/references.actions';
import { selectReferenceList, selectCitationlist } from '../store/references.reducer';

@Injectable({
  providedIn: 'root',
})
export class ReferenceManagementService {
  private view: EditorView;

  constructor(
    private apollo: Apollo,
    private store: Store,
    private injector: Injector,
    private renderingService: RenderingService,
  ) {}

  /** Returns the CSL for a list of citation items. */
  async getCSL(citationItems: CitationItem[]): Promise<any> {
    const references = await firstValueFrom(this.store.select(selectReferenceList));
    return references
      ?.filter((reference) =>
        citationItems.some((citationItem) => citationItem.id === reference.csl?.id),
      )
      .map((ref) => ref?.csl);
  }

  async getReferencesEditCitation(citationItems: CitationItem[]): Promise<any> {
    const references = await firstValueFrom(this.store.select(selectReferenceList));
    return references
      ?.map(({ csl }) => csl)
      .filter((reference) =>
        citationItems.some((citationItem) => citationItem.id === reference?.id),
      )
      .map((ref) => ref);
  }

  /** Updates a citation in the editor. */
  async updateCitation(
    citation: CitationWithId & { plainCitationHtml?: string | null },
    options?: { preserveNodeContent?: boolean },
  ): Promise<void> {
    const csls = await this.getCSL(citation.citationItems);
    const renderedCitation = this.renderingService.render(citation.citationItems, csls);
    const useCustomCitation = citation?.citationMode === 'custom';
    const plainCitationText =
      typeof citation?.plainCitation === 'string' ? citation.plainCitation.trim() : '';
    const plainCitationHtml =
      typeof citation?.plainCitationHtml === 'string' ? citation.plainCitationHtml.trim() : '';
    const plainCitation = useCustomCitation
      ? plainCitationText || renderedCitation
      : renderedCitation;

    this.store.dispatch(
      updateCitation({
        citation: {
          ...citation,
          plainCitation,
          citationMode: useCustomCitation ? 'custom' : 'generated',
        },
      }),
    );
    if (this.view) {
      let textUpdate: { text?: string; html?: string } | string | undefined;
      if (useCustomCitation) {
        textUpdate =
          plainCitationHtml.length > 0
            ? { text: plainCitation, html: plainCitationHtml }
            : plainCitation;
      } else {
        textUpdate = options?.preserveNodeContent === true ? undefined : plainCitation;
      }
      updateCitationByIdCommand(citation, textUpdate)(this.view.state, this.view.dispatch);
    }
  }

  /** Returns the rendered plain citation text for current citation items. */
  async getRenderedCitationText(citationItems: CitationItem[]): Promise<string> {
    const csls = await this.getCSL(citationItems);
    return this.renderingService.render(citationItems, csls);
  }

  /**
   * Updates all citations in the editor that use a specific reference ID.
   */
  async updateAllCitationsForReference(referenceId: string): Promise<void> {
    if (!this.view) {
      console.warn('Editor view not available for updating citations');
      return;
    }

    const citations = await firstValueFrom(this.store.select(selectCitationlist));

    for (const citation of citations || []) {
      const containsReference = citation.citationItems.some((item: any) => item.id === referenceId);
      if (containsReference) {
        const csls = await this.getCSL(citation.citationItems);
        const plainCitation = this.renderingService.render(citation.citationItems, csls);

        this.store.dispatch(updateCitation({ citation: { ...citation, plainCitation } }));
        updateCitationByIdSilentCommand(citation, plainCitation)(
          this.view.state,
          this.view.dispatch,
        );
      }
    }
  }

  /**
   * Persists an updated reference in the store and refreshes dependent citations.
   */
  async updateReference(updatedReference: Reference): Promise<Reference> {
    const normalizedReference: Reference = {
      ...updatedReference,
      csl: updatedReference.csl,
      bib: updatedReference.bib,
    };

    const currentReferences = await firstValueFrom(this.store.select(selectReferenceList));
    const nextReferences = currentReferences.map((reference) =>
      reference.key === normalizedReference.key ? normalizedReference : reference,
    );
    this.store.dispatch(addReferences({ references: nextReferences }));

    const referenceId = normalizedReference.csl?.id;
    if (referenceId) {
      await this.updateAllCitationsForReference(referenceId);
    }

    return normalizedReference;
  }

  sortBibliographyNodes(orderedIds: string[]): void {
    if (!this.view) return;

    const state = this.view.state;

    const bibliographyPart = (() => {
      let found: { node: ProsemirrorNode; pos: number } | null = null;
      state.doc.descendants((node, pos) => {
        if (found) return false;
        if (node.type.name === SFNodeType.part && node.attrs?.type === 'bibliography') {
          found = { node, pos };
          return false;
        }
        return true;
      });
      return found;
    })() as { node: ProsemirrorNode; pos: number } | null;
    if (!bibliographyPart) return;

    const partNode = bibliographyPart.node as ProsemirrorNode;
    const partPos = bibliographyPart.pos;

    const refNodesByRefId = new Map<string, ProsemirrorNode>();
    const refNodesInOrder: ProsemirrorNode[] = [];
    let firstRefAbsPos: number | null = null;
    let lastRefAbsPos: number | null = null;
    let lastRefNodeSize = 0;

    partNode.descendants((node, relPos) => {
      if (node.type.name !== SFNodeType.reference) return true;
      const refId: string | undefined = node.attrs?.refId;
      if (refId) refNodesByRefId.set(refId, node);
      refNodesInOrder.push(node);
      const absPos = partPos + 1 + relPos;
      if (firstRefAbsPos === null) firstRefAbsPos = absPos;
      lastRefAbsPos = absPos;
      lastRefNodeSize = node.nodeSize;
      return false;
    });

    if (refNodesInOrder.length === 0 || firstRefAbsPos === null) return;

    const sortedNodes: ProsemirrorNode[] = [];
    for (const id of orderedIds) {
      const node = refNodesByRefId.get(id);
      if (node) sortedNodes.push(node);
    }
    for (const node of refNodesInOrder) {
      const refId: string | undefined = node.attrs?.refId;
      if (!refId || !orderedIds.includes(refId)) sortedNodes.push(node);
    }

    if (
      sortedNodes.length === refNodesInOrder.length &&
      sortedNodes.every((n, i) => n === refNodesInOrder[i])
    )
      return;

    const from = firstRefAbsPos;
    const to = lastRefAbsPos! + lastRefNodeSize;
    this.view.dispatch(state.tr.replaceWith(from, to, Fragment.from(sortedNodes)));
  }

  getCitationContent(
    citationID: string,
  ): { text: string; html: string; citationMode: 'generated' | 'custom' } | null {
    if (!this.view || !citationID) {
      return null;
    }

    let content: { text: string; html: string; citationMode: 'generated' | 'custom' } | null = null;
    const canSerializeHtml = typeof document !== 'undefined';
    const serializer = canSerializeHtml ? DOMSerializer.fromSchema(this.view.state.schema) : null;
    this.view.state.doc.nodesBetween(0, this.view.state.doc.nodeSize - 2, (node) => {
      if (node.type.name !== SFNodeType.citation || node.attrs?.id !== citationID) {
        return true;
      }

      content = {
        text: node.textContent ?? '',
        html:
          canSerializeHtml && serializer
            ? (() => {
                const container = document.createElement('div');
                container.appendChild(serializer.serializeFragment(node.content));
                return container.innerHTML ?? '';
              })()
            : '',
        citationMode: node.attrs?.citationMode === 'custom' ? 'custom' : 'generated',
      };
      return false;
    });

    return content;
  }

  /**
   * @returns the plugins and views needed by this module.
   */
  getProsemirror(injector): {
    views: { [nodeType: string]: NodeViewConstructor };
    plugins: Plugin[];
  } {
    return {
      plugins: [
        getReferencePlugin(this.store, (editorView: EditorView) => (this.view = editorView)),
      ],
      views: {
        citation: (node, nodeView, getPos) => new CitationView(node, nodeView, getPos, injector),
      },
    };
  }
}
